import { assertNever } from "@sa-nu/shared-utils/misc";
import type { ComponentProps } from "react";
import { memo, useMemo } from "react";

import { useAccommodationCounts } from "../filters/_hooks/use-accommodation-counts";
import { useAccommodationFilterContext } from "../filters/accommodation-filter-context";

import type {
  AreaSuggestionDivider,
  FilteredArea,
} from "./_utils/area-filtering";
import { getAreaSuggestionDivider } from "./_utils/area-filtering";
import { sortAreasWithAreaFilter } from "./_utils/area-sorting";
import { AreaSection } from "./area-section";
import { CalendarAreaSuggestionDivider } from "./calendar-area-suggestion";
import { CalendarEmptyState } from "./calendar-empty-state";

type AreaSectionProps = ComponentProps<typeof AreaSection>;

type CalendarAreaListProps = Omit<
  AreaSectionProps,
  "area" | "accommodationCount" | "hidden" | "dimmed"
> & {
  allAreas: FilteredArea[];
  displayAreas: FilteredArea[];
  displayAreaMap: Map<string, FilteredArea>;
  hasAvailabilityData: boolean;
};

function shouldDimUnselectedAreas(
  areaSuggestion: AreaSuggestionDivider | null,
  areaFilter: ReadonlySet<string>,
): boolean {
  if (areaSuggestion === null) {
    // フィルタ未設定 or 非選択に空きなし
    return areaFilter.size > 0;
  }
  switch (areaSuggestion.kind) {
    case "area-only":
      return true;
    case "with-conditions":
      return !areaSuggestion.selectedAreaAllEmpty;
    default:
      assertNever(areaSuggestion);
  }
}

// カレンダー部分のコンポーネント
// エリアの表示制御や、0件表示画面の表示制御を担う。
export const CalendarAreaList = memo(function CalendarAreaList({
  allAreas,
  displayAreas,
  displayAreaMap,
  hasAvailabilityData,
  cico,
  loadingAvailability,
  ...areaSectionProps
}: CalendarAreaListProps) {
  const { hasAccommodationFilter, areaFilter, matchesAccommodationFilter } =
    useAccommodationFilterContext();
  const totalOnly = cico === null && !hasAccommodationFilter;
  const { isFilteredEmpty, accommodationCountMap } = useAccommodationCounts(
    { allAreas, displayAreas },
    {
      totalOnly,
      hasAccommodationFilter,
      matchesAccommodationFilter,
    },
  );

  // 表示制御: CICO絞り込み結果が0件ならEmpty Stateを表示
  const shouldShowCICOEmptyState =
    cico !== null &&
    hasAvailabilityData &&
    !loadingAvailability &&
    displayAreas.length === 0;

  // フィルターで0件（CICO結果はあるがフィルターで全部消える）
  const shouldShowFilterEmptyState =
    !shouldShowCICOEmptyState && isFilteredEmpty;

  // いずれかの空状態を表示すべきか
  const shouldShowEmptyState =
    shouldShowCICOEmptyState || shouldShowFilterEmptyState;

  // 選択/非選択エリア間のサジェスションディバイダー
  const areaSuggestion = useMemo(
    () =>
      shouldShowEmptyState
        ? null
        : getAreaSuggestionDivider({
            allAreas,
            areaFilter,
            accommodationCountMap,
            totalOnly,
          }),
    [
      shouldShowEmptyState,
      allAreas,
      areaFilter,
      accommodationCountMap,
      totalOnly,
    ],
  );

  const dimUnselected = shouldDimUnselectedAreas(areaSuggestion, areaFilter);

  const sortedAreas = useMemo(
    () => sortAreasWithAreaFilter(allAreas, areaFilter),
    [allAreas, areaFilter],
  );

  const selectedAreas = useMemo(
    () => sortedAreas.filter((a) => areaFilter.has(a.slug)),
    [sortedAreas, areaFilter],
  );
  const unselectedAreas = useMemo(
    () => sortedAreas.filter((a) => !areaFilter.has(a.slug)),
    [sortedAreas, areaFilter],
  );

  const renderAreaSection = (area: FilteredArea, dimmed: boolean) => (
    <AreaSection
      key={area.id}
      area={displayAreaMap.get(area.id) ?? area}
      accommodationCount={accommodationCountMap.get(area.id)}
      hidden={shouldShowEmptyState || displayAreaMap.get(area.id) == null}
      dimmed={dimmed}
      cico={cico}
      loadingAvailability={loadingAvailability}
      {...areaSectionProps}
    />
  );

  return (
    <>
      {shouldShowEmptyState && (
        <CalendarEmptyState showSearchDates={cico !== null} />
      )}
      {/* CI/CO絞り込み中にエリアがアンマウントされるとCollapsibleの状態が消えるのと、
          再レンダリングすると描画のコストがかかるため、エリアのマウントはしたまま
          display: hiddenで表示・非表示を切り替えることにした。
          そのためallAreasでmapし、displayAreasにデータあれば表示・なければ非表示にする。 */}
      {selectedAreas.map((area) => renderAreaSection(area, false))}
      {areaSuggestion && (
        <CalendarAreaSuggestionDivider suggestion={areaSuggestion} />
      )}
      {unselectedAreas.map((area) => renderAreaSection(area, dimUnselected))}
    </>
  );
});
